import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Phone, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import CapabilityStatementModal from "./CapabilityStatementModal";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_1cb4de8f-8c80-44d3-8d05-6a8d3846de63/artifacts/lp2h710t_NEW%20PWC%20logo%20with%20welder.png";

const navLinks = [
  { name: "Home", path: "/" },
  { name: "Services", path: "/services" },
  { name: "Products", path: "/products" },
  { name: "About", path: "/about" },
  { name: "Contact", path: "/contact" },
];

// Get page title from path
const getPageTitle = (pathname) => {
  const titles = {
    '/': 'Home',
    '/services': 'Services',
    '/products': 'Products',
    '/about': 'About',
    '/contact': 'Contact',
  };
  return titles[pathname] || 'PWC';
};

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [showCapabilityModal, setShowCapabilityModal] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const isActive = (path) => location.pathname === path;
  const isHomePage = location.pathname === '/';

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A] border-b border-[#2A2A2A]" data-testid="navbar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Mobile: Back button (when not on home) OR Logo (when on home) */}
            <div className="lg:hidden flex items-center">
              {!isHomePage ? (
                <button
                  onClick={handleBack}
                  className="flex items-center justify-center w-10 h-10 -ml-2 text-white"
                  data-testid="mobile-back-btn"
                >
                  <ArrowLeft size={28} strokeWidth={2.5} />
                </button>
              ) : (
                <Link to="/" className="flex items-center gap-3" data-testid="logo-link-mobile">
                  <img 
                    src={LOGO_URL}
                    alt="PWC Fabrication & Engineering"
                    className="h-14 w-auto"
                  />
                </Link>
              )}
            </div>

            {/* Mobile: Centered page title (when not on home) */}
            {!isHomePage && (
              <div className="lg:hidden absolute left-1/2 transform -translate-x-1/2">
                <span className="text-white font-bold text-lg tracking-wide uppercase">
                  {getPageTitle(location.pathname)}
                </span>
              </div>
            )}

            {/* Mobile: Company name on home page */}
            {isHomePage && (
              <div className="lg:hidden flex-1 ml-3">
                <span className="text-white font-bold text-sm tracking-wide">PWC Fabrication/Engineering</span>
              </div>
            )}

            {/* Desktop Logo */}
            <Link to="/" className="hidden lg:flex items-center gap-3" data-testid="logo-link">
              <img 
                src={LOGO_URL}
                alt="PWC Fabrication & Engineering"
                className="h-14 w-auto"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`font-semibold text-sm uppercase tracking-wider transition-colors relative py-2 ${
                    isActive(link.path)
                      ? "text-[#E63946]"
                      : "text-[#A3A3A3] hover:text-white"
                  }`}
                  data-testid={`nav-${link.name.toLowerCase()}`}
                >
                  {link.name}
                  {isActive(link.path) && (
                    <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#E63946]" />
                  )}
                </Link>
              ))}
            </div>

            {/* Desktop CTA */}
            <div className="hidden lg:flex items-center gap-4">
              <a 
                href="tel:0402275003" 
                className="flex items-center gap-2 text-[#A3A3A3] hover:text-white transition-colors"
                data-testid="phone-link"
              >
                <Phone size={18} className="text-[#E63946]" />
                <span className="font-semibold">0402 275 003</span>
              </a>
              <Link to="/contact">
                <Button 
                  className="bg-[#E63946] hover:bg-[#D62839] text-white font-bold uppercase tracking-wider px-6 py-2 rounded-full btn-glow"
                  data-testid="get-quote-btn"
                >
                  Get a Quote
                </Button>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="lg:hidden p-2 text-white"
              data-testid="mobile-menu-btn"
            >
              {isOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="lg:hidden bg-[#121212] border-t border-[#2A2A2A]" data-testid="mobile-menu">
            <div className="px-4 py-4 space-y-3">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsOpen(false)}
                  className={`block py-3 px-4 font-semibold text-sm uppercase tracking-wider rounded-xl transition-colors ${
                    isActive(link.path)
                      ? "text-[#E63946] bg-[#1A1A1A]"
                      : "text-[#A3A3A3] hover:text-white hover:bg-[#1A1A1A]"
                  }`}
                  data-testid={`mobile-nav-${link.name.toLowerCase()}`}
                >
                  {link.name}
                </Link>
              ))}
              {/* Capability Statement - Mobile Only */}
              <button
                onClick={() => {
                  setIsOpen(false);
                  setShowCapabilityModal(true);
                }}
                className="block w-full text-left py-3 px-4 font-semibold text-sm uppercase tracking-wider rounded-xl transition-colors text-[#A3A3A3] hover:text-white hover:bg-[#1A1A1A]"
                data-testid="mobile-nav-capability"
              >
                Capability Statement
              </button>
              <div className="pt-4 border-t border-[#2A2A2A]">
                <a 
                  href="tel:0402275003" 
                  className="flex items-center gap-2 text-white py-3 px-4"
                >
                  <Phone size={18} className="text-[#E63946]" />
                  <span className="font-semibold">0402 275 003</span>
                </a>
                <Link to="/contact" onClick={() => setIsOpen(false)}>
                  <Button 
                    className="w-full mt-4 bg-[#E63946] hover:bg-[#D62839] text-white font-bold uppercase tracking-wider py-3 rounded-full"
                    data-testid="mobile-get-quote-btn"
                  >
                    Get a Quote
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Capability Statement Modal */}
      <CapabilityStatementModal 
        isOpen={showCapabilityModal} 
        onClose={() => setShowCapabilityModal(false)} 
      />
    </>
  );
}
